import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";

export type SenecButtonLevel = "primary" | "secondary" | "tertiary" | "warning";
export type SenecButtonSize = "small" | "medium";
export type SenecButtonColorTheme = "light" | "medium" | "dark";
export type ReducedButtonColorTheme = Extract<SenecButtonColorTheme, "light" | "dark">;

const makeTransparent = (hex: string, alpha: string) => {
    return hex + alpha;
};

interface ButtonColorScheme {
    primaryMain: string;
    primaryLight: string;
    primaryDark: string;
    primaryText: string;
    secondaryMain: string;
    secondaryLight: string;
    secondaryDark: string;
    secondaryText: string;
    tertiaryMain: string;
    tertiaryLight: string;
    tertiaryDark: string;
    tertiaryText: string;
    warningMain: string;
    warningLight: string;
    warningDark: string;
    warningText: string;
}

export const buttonColors = (theme: SenecFPPTheme): Record<ReducedButtonColorTheme, ButtonColorScheme> => {
    return {
        light: {
            primaryMain: theme.palette.primary.main,
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.azur90.main,
            primaryText: theme.palette.white.main,
            secondaryMain: theme.palette.white.main,
            secondaryLight: theme.palette.azur60.main,
            secondaryDark: theme.palette.azur90.main,
            secondaryText: theme.palette.primary.main,
            tertiaryMain: theme.palette.primary.main,
            tertiaryLight: theme.palette.azur60.main,
            tertiaryDark: theme.palette.azur90.main,
            tertiaryText: theme.palette.primary.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
        dark: {
            primaryMain: theme.palette.white.main,
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.neutralGray20.main,
            primaryText: theme.palette.primary.main,
            secondaryMain: theme.palette.primary.main,
            secondaryLight: theme.palette.azur60.main,
            secondaryDark: theme.palette.neutralGray20.main,
            secondaryText: theme.palette.white.main,
            tertiaryMain: "transparent",
            tertiaryLight: theme.palette.azur60.main,
            tertiaryDark: theme.palette.neutralGray20.main,
            tertiaryText: theme.palette.white.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
    };
};

export const functionalButtonColors = (theme: SenecFPPTheme): Record<SenecButtonColorTheme, ButtonColorScheme> => {
    return {
        light: {
            primaryMain: theme.palette.primary.main,
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.azur90.main,
            primaryText: theme.palette.white.main,
            secondaryMain: theme.palette.neutralGray00.main,
            secondaryLight: theme.palette.neutralGray20.main,
            secondaryDark: theme.palette.azur90.main,
            secondaryText: theme.palette.neutralGray100.main,
            tertiaryMain: "transparent",
            tertiaryLight: theme.palette.neutralGray20.main,
            tertiaryDark: theme.palette.azur90.main,
            tertiaryText: theme.palette.neutralGray100.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
        medium: {
            primaryMain: theme.palette.primary.main,
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.azur90.main,
            primaryText: theme.palette.white.main,
            secondaryMain: theme.palette.neutralGray10.main,
            secondaryLight: theme.palette.neutralGray30.main,
            secondaryDark: theme.palette.azur90.main,
            secondaryText: theme.palette.neutralGray100.main,
            tertiaryMain: "transparent",
            tertiaryLight: theme.palette.neutralGray30.main,
            tertiaryDark: theme.palette.azur90.main,
            tertiaryText: theme.palette.neutralGray100.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
        dark: {
            primaryMain: theme.palette.white.main,
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.azur90.main,
            primaryText: theme.palette.primary.main,
            secondaryMain: makeTransparent(theme.palette.neutralGray10.main, "33"),
            secondaryLight: makeTransparent(theme.palette.neutralGray10.main, "66"),
            secondaryDark: theme.palette.azur90.main,
            secondaryText: theme.palette.white.main,
            tertiaryMain: "transparent",
            tertiaryLight: makeTransparent(theme.palette.neutralGray10.main, "66"),
            tertiaryDark: theme.palette.azur90.main,
            tertiaryText: theme.palette.white.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
    };
};

export const segmentedButtonColors = (theme: SenecFPPTheme): Record<ReducedButtonColorTheme, ButtonColorScheme> => {
    return {
        light: {
            primaryMain: theme.palette.neutralGray05.main,
            primaryLight: theme.palette.neutralGray000.main,
            primaryDark: theme.palette.azur90.main,
            primaryText: theme.palette.black.main,
            secondaryMain: theme.palette.neutralGray20.main,
            secondaryLight: theme.palette.azur60.main,
            secondaryDark: theme.palette.azur90.main,
            secondaryText: theme.palette.black.main,
            tertiaryMain: theme.palette.primary.main,
            tertiaryLight: theme.palette.azur60.main,
            tertiaryDark: theme.palette.azur90.main,
            tertiaryText: theme.palette.primary.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
        dark: {
            primaryMain: makeTransparent(theme.palette.neutralGray100.main, "99"),
            primaryLight: theme.palette.azur60.main,
            primaryDark: theme.palette.neutralGray20.main,
            primaryText: theme.palette.black.main,
            secondaryMain: makeTransparent(theme.palette.white.main, "50"),
            secondaryLight: theme.palette.azur60.main,
            secondaryDark: theme.palette.neutralGray20.main,
            secondaryText: theme.palette.white.main,
            tertiaryMain: "transparent",
            tertiaryLight: theme.palette.azur60.main,
            tertiaryDark: theme.palette.neutralGray20.main,
            tertiaryText: theme.palette.white.main,
            warningMain: theme.palette.error.main,
            warningLight: theme.palette.tarocco60.main,
            warningDark: theme.palette.tarocco90.main,
            warningText: theme.palette.white.main,
        },
    };
};
